if (!customElements.get('omni-v2-controller')) {
  customElements.define(
    'omni-v2-controller',
    class OmniV2Controller extends HTMLElement {
      connectedCallback() {
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        
        // 1. Reveal Animations
        this.revealItems = [...this.querySelectorAll('[data-v2-reveal]')];
        if (this.reducedMotion || !('IntersectionObserver' in window)) {
          this.revealItems.forEach((item) => item.classList.add('is-revealed'));
        } else {
          this.revealObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                entry.target.classList.add('is-revealed');
                this.revealObserver.unobserve(entry.target);
              });
            },
            { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
          );
          this.revealItems.forEach((item) => this.revealObserver.observe(item));
        }

        // 2. Draggable Image Quality Comparison Inspector
        this.initQualitySlider();

        // 3. Interactive Top-Down Vehicle Scanning HUD
        this.initMultiviewHud();

        // 4. Parallax Scroll Variables
        if (!this.reducedMotion && this.hasAttribute('data-scroll-parallax')) {
          this.currentScroll = 0.5;
          this.targetScroll = 0.5;
          this.isLooping = false;
          
          this.onScroll = this.onScroll.bind(this);
          this.updateLoop = this.updateLoop.bind(this);
          
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll, { passive: true });
          this.onScroll();
        }
      }

      disconnectedCallback() {
        this.revealObserver?.disconnect();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.isLooping = false;
        this.destroySlider();
      }

      // Parallax Loop
      onScroll() {
        const rect = this.getBoundingClientRect();
        const viewport = window.innerHeight || 1;
        const progress = Math.min(1, Math.max(0, (viewport - rect.top) / (viewport + rect.height)));
        this.targetScroll = progress;
        
        if (!this.isLooping) {
          this.isLooping = true;
          requestAnimationFrame(this.updateLoop);
        }
      }

      updateLoop() {
        if (!this.isLooping) return;
        
        const diff = this.targetScroll - this.currentScroll;
        if (Math.abs(diff) < 0.0001) {
          this.currentScroll = this.targetScroll;
          this.isLooping = false;
        } else {
          this.currentScroll += diff * 0.085; // smooth damping factor
        }
        
        const centered = this.currentScroll * 2 - 1;
        this.style.setProperty('--omni-scroll', this.currentScroll.toFixed(4));
        this.style.setProperty('--omni-shift', `${(centered * 40).toFixed(2)}px`);
        this.style.setProperty('--omni-rotate', `${(centered * 5).toFixed(2)}deg`);
        
        if (this.isLooping) {
          requestAnimationFrame(this.updateLoop);
        }
      }

      // Image Comparison Slider Logic
      initQualitySlider() {
        this.inspector = this.querySelector('.js-quality-inspector');
        if (!this.inspector) return;

        this.afterContainer = this.inspector.querySelector('.js-quality-after');
        this.handle = this.inspector.querySelector('.js-quality-handle');
        if (!this.afterContainer || !this.handle) return;

        this.isDragging = false;
        
        this.startDrag = this.startDrag.bind(this);
        this.onDrag = this.onDrag.bind(this);
        this.stopDrag = this.stopDrag.bind(this);

        this.inspector.addEventListener('mousedown', this.startDrag);
        this.inspector.addEventListener('touchstart', this.startDrag, { passive: true });
        
        window.addEventListener('mousemove', this.onDrag);
        window.addEventListener('touchmove', this.onDrag, { passive: false });
        
        window.addEventListener('mouseup', this.stopDrag);
        window.addEventListener('touchend', this.stopDrag);
      }

      destroySlider() {
        if (!this.inspector) return;
        this.inspector.removeEventListener('mousedown', this.startDrag);
        this.inspector.removeEventListener('touchstart', this.startDrag);
        window.removeEventListener('mousemove', this.onDrag);
        window.removeEventListener('touchmove', this.onDrag);
        window.removeEventListener('mouseup', this.stopDrag);
        window.removeEventListener('touchend', this.stopDrag);
      }

      startDrag(e) {
        // Prevent default touch scrolling inside the inspector bounds to ensure smooth dragging
        if (e.type === 'touchstart') {
          this.isDragging = true;
        } else {
          e.preventDefault();
          this.isDragging = true;
        }
        this.updateSliderPosition(e);
      }

      onDrag(e) {
        if (!this.isDragging) return;
        if (e.type === 'touchmove') {
          // Prevent scroll
          e.preventDefault();
        }
        this.updateSliderPosition(e);
      }

      stopDrag() {
        this.isDragging = false;
      }

      updateSliderPosition(e) {
        const rect = this.inspector.getBoundingClientRect();
        let clientX = 0;
        
        if (e.touches && e.touches[0]) {
          clientX = e.touches[0].clientX;
        } else {
          clientX = e.clientX;
        }

        const positionX = clientX - rect.left;
        let percentage = (positionX / rect.width) * 100;
        
        // Bounds clamping
        if (percentage < 0) percentage = 0;
        if (percentage > 100) percentage = 100;

        // Apply style changes directly (using clip-path polygon)
        this.afterContainer.style.clipPath = `polygon(0 0, ${percentage}% 0, ${percentage}% 100%, 0 100%)`;
        this.handle.style.left = `${percentage}%`;
      }

      // Multiview HUD Logic
      initMultiviewHud() {
        this.multiview = this.querySelector('.js-multiview-root');
        if (!this.multiview) return;

        this.tabs = [...this.querySelectorAll('.js-multiview-tab')];
        this.panes = [...this.querySelectorAll('.js-multiview-pane')];
        this.hotspots = [...this.querySelectorAll('.js-multiview-hotspot')];
        this.readout = this.querySelector('.js-telemetry-readout');

        const selectView = (viewName) => {
          this.multiview.setAttribute('data-active-view', viewName);

          // Update tabs
          this.tabs.forEach((tab) => {
            tab.classList.toggle('is-active', tab.getAttribute('data-view-target') === viewName);
          });

          // Update hotspots
          this.hotspots.forEach((hs) => {
            hs.classList.toggle('is-active', hs.getAttribute('data-view-target') === viewName);
          });

          // Update text panes
          this.panes.forEach((pane) => {
            pane.classList.toggle('is-active', pane.getAttribute('data-view-pane') === viewName);
          });

          // Update simulated telemetry
          if (this.readout) {
            let info = '';
            switch (viewName) {
              case 'front':
                info = 'LENS_ID: FRONT_01\nSTATUS: ACTIVE\nHDR: ENABLED\nFPS: 60 / SEC\nFOCAL: 2.8MM / f1.6\nTELEMETRY: ONLINE';
                break;
              case 'cabin':
                info = 'LENS_ID: CABIN_02\nSTATUS: ACTIVE\nNIGHT_VISION: AUTO\nFPS: 30 / SEC\nFOCAL: 2.0MM / f1.8\nTELEMETRY: ONLINE';
                break;
              case 'rear':
                info = 'LENS_ID: REAR_03\nSTATUS: ACTIVE\nREAR_SCAN: ACTIVE\nFPS: 60 / SEC\nFOCAL: 3.2MM / f2.0\nTELEMETRY: ONLINE';
                break;
              case 'parking':
                info = 'LENS_ID: SYSTEM_STBY\nSTATUS: MONITORING\nG-SENSOR: ARMED\nFPS: 5 (LOW_PWR)\nBATTERY: 13.8V\nTELEMETRY: GUARDING';
                break;
              case 'side':
                info = 'LENS_ID: SIDE_04\nSTATUS: MONITORING\nSIDE_SCAN: ACTIVE\nFPS: 15 / SEC\nBATTERY: 13.8V\nTELEMETRY: GUARDING';
                break;
            }
            this.readout.innerText = info;
          }
        };

        // Attach click triggers to tabs
        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => {
            const viewName = tab.getAttribute('data-view-target');
            selectView(viewName);
          });
        });

        // Attach click triggers to hotspots
        this.hotspots.forEach((hs) => {
          hs.addEventListener('click', () => {
            const viewName = hs.getAttribute('data-view-target');
            selectView(viewName);
          });
        });

        // Initialize default view
        selectView('front');
      }
    }
  );
}
